import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Search, 
  Plus, 
  Download, 
  Copy, 
  Eye, 
  Trash2, 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Gift,
  X,
  Loader,
  Check,
  Info,
  Printer,
  Filter
} from 'lucide-react';
import { voucherService, booksService } from '../services/api';
import { format, parseISO } from 'date-fns';

// Success Modal Component
const SuccessModal = ({ isOpen, onClose, title, message, code = null }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="h-6 w-6 text-green-600" />
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
            {title}
          </h3>
          
          <p className="text-gray-600 text-center mb-4">
            {message}
          </p>

          {code && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="text-sm font-medium text-gray-700 mb-1">Voucher Code:</div>
              <div className="font-mono text-lg font-bold text-[#1d1d1d] text-center">{code}</div>
            </div>
          )}
          
          <button
            onClick={onClose}
            className="w-full bg-[#1d1d1d] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Error Modal Component
const ErrorModal = ({ isOpen, onClose, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
            {title}
          </h3>
          
          <p className="text-gray-600 text-center mb-4">
            {message}
          </p>
          
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

// Info Modal Component
const InfoModal = ({ isOpen, onClose, title, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Info className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
            {title}
          </h3>
          
          <p className="text-gray-600 text-center mb-4">
            {message}
          </p>
          
          <button
            onClick={onClose}
            className="w-full bg-[#1d1d1d] text-white px-4 py-2.5 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  isDestructive = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className={`h-12 w-12 ${isDestructive ? 'bg-red-100' : 'bg-yellow-100'} rounded-full flex items-center justify-center`}>
              <AlertCircle className={`h-6 w-6 ${isDestructive ? 'text-red-600' : 'text-yellow-600'}`} />
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
            {title}
          </h3>
          
          <p className="text-gray-600 text-center mb-6">
            {message}
          </p>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                isDestructive 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-[#1d1d1d] text-white hover:bg-gray-800'
              }`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Print Modal Component - Optimized for PVC Card Printing (85.6mm x 54mm)
const PrintModal = ({ isOpen, onClose, vouchers }) => {
  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [printLayout, setPrintLayout] = useState('four-per-page');
  const [cardsPerPage, setCardsPerPage] = useState(4);
  const [showBarcode, setShowBarcode] = useState(true);
  const [showScratchArea, setShowScratchArea] = useState(true);
  const [cardStyle, setCardStyle] = useState('modern');

  // Move useEffect to top level - FIXED
  useEffect(() => {
    if (isOpen && vouchers) {
      setSelectedVouchers(vouchers.filter(v => v.status === 'active').map(v => v.id));
    }
  }, [isOpen, vouchers]);

  // Update cards per page based on layout
  useEffect(() => {
    switch(printLayout) {
      case 'single':
        setCardsPerPage(1);
        break;
      case 'two-per-page':
        setCardsPerPage(2);
        break;
      case 'four-per-page':
        setCardsPerPage(4);
        break;
      case 'nine-per-page':
        setCardsPerPage(9);
        break;
      default:
        setCardsPerPage(4);
    }
  }, [printLayout]);

  const toggleVoucher = (id) => {
    setSelectedVouchers(prev => 
      prev.includes(id) 
        ? prev.filter(vId => vId !== id)
        : [...prev, id]
    );
  };

  const selectAllActive = () => {
    const activeVouchers = vouchers.filter(v => v.status === 'active');
    setSelectedVouchers(activeVouchers.map(v => v.id));
  };

  const clearSelection = () => {
    setSelectedVouchers([]);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const selectedData = vouchers.filter(v => selectedVouchers.includes(v.id));
    const now = new Date();
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Silk Space Gift Cards - PVC Print</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
          
          @media print {
            @page {
              size: A4;
              margin: 0.25in;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            body {
              font-family: 'Inter', sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              color: #000;
            }
            
            /* PVC Card Dimensions: 85.6mm x 54mm (Standard credit card size) */
            .pvc-card {
              width: 85.6mm;
              height: 54mm;
              background: white;
              border: 1px solid #000;
              border-radius: 4mm;
              position: relative;
              overflow: hidden;
              page-break-inside: avoid;
              break-inside: avoid;
              margin: 5mm;
              float: left;
              box-sizing: border-box;
              box-shadow: 0 2mm 6mm rgba(0,0,0,0.1);
            }
            
            /* Card layout for multiple cards per page */
            .single .pvc-card {
              margin: 20mm auto;
            }
            
            .two-per-page .pvc-card {
              margin: 10mm;
            }
            
            .four-per-page .pvc-card {
              margin: 5mm;
            }
            
            .nine-per-page .pvc-card {
              margin: 2mm;
            }
            
            /* Modern Card Design */
            .card-background {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(135deg, #f8f8f8 0%, #ffffff 100%);
              z-index: 1;
            }
            
            .card-pattern {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-image: 
                radial-gradient(circle at 20% 80%, rgba(0,0,0,0.03) 2mm, transparent 4mm),
                radial-gradient(circle at 80% 20%, rgba(0,0,0,0.03) 2mm, transparent 4mm);
              z-index: 2;
            }
            
            /* Card Header */
            .card-header {
              height: 10mm;
              background: linear-gradient(90deg, #000000 0%, #333333 100%);
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 0 4mm;
              position: relative;
              z-index: 3;
              border-radius: 4mm 4mm 0 0;
            }
            
            .card-title {
              color: white;
              font-size: 3.2mm;
              font-weight: 800;
              letter-spacing: 0.3px;
              text-transform: uppercase;
            }
            
            /* Logo */
            .logo-container {
              display: flex;
              align-items: center;
              gap: 2mm;
            }
            
            .logo-circle {
              width: 6mm;
              height: 6mm;
              background: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 900;
              font-size: 2.5mm;
              color: #000;
            }
            
            /* Card Body */
            .card-body {
              padding: 4mm;
              height: calc(54mm - 10mm);
              box-sizing: border-box;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              position: relative;
              z-index: 3;
            }
            
            /* Card Details - Enhanced Layout */
            .card-details {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 3mm;
              margin-bottom: 4mm;
            }
            
            .detail-group {
              display: flex;
              flex-direction: column;
              gap: 1mm;
            }
            
            .detail-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .detail-label {
              font-size: 2mm;
              font-weight: 600;
              color: #666;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
            
            .detail-value {
              font-size: 2.5mm;
              font-weight: 700;
              color: #000;
              text-align: right;
            }
            
            .value-highlight {
              background: #000;
              color: white;
              padding: 1mm 2mm;
              border-radius: 1mm;
              font-weight: 800;
            }
            
            /* Code Section - More Prominent */
            .code-section {
              text-align: center;
              margin: 3mm 0;
              background: #f5f5f5;
              border-radius: 2mm;
              padding: 3mm;
              border: 1px solid #e0e0e0;
            }
            
            .code-display {
              font-family: 'Courier New', monospace;
              font-size: 5mm;
              font-weight: 900;
              letter-spacing: 3px;
              color: #000;
              padding: 2mm;
              background: white;
              border-radius: 1.5mm;
              border: 1px solid #ddd;
              margin: 1mm 0;
              display: inline-block;
              min-width: 60mm;
            }
            
            .scratch-area {
              background: repeating-linear-gradient(
                45deg,
                #888,
                #888 2px,
                #aaa 2px,
                #aaa 4px
              );
              border: 1px solid #666;
              border-radius: 2mm;
              padding: 4mm;
              margin-top: 3mm;
              text-align: center;
              font-size: 2mm;
              font-weight: 700;
              color: white;
              position: relative;
              overflow: hidden;
            }
            
            .scratch-area::before {
              content: 'SCRATCH TO REVEAL';
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              font-weight: 900;
              color: rgba(255,255,255,0.9);
              text-shadow: 0 1px 2px rgba(0,0,0,0.3);
              letter-spacing: 1px;
              font-size: 2.5mm;
            }
            
            /* Enhanced Barcode - More Visible */
            .barcode-section {
              margin-top: 3mm;
              text-align: center;
              padding: 2mm;
              background: white;
              border-radius: 1.5mm;
              border: 1px solid #ddd;
            }
            
            .barcode-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 1mm;
            }
            
            .barcode-lines {
              display: inline-block;
              padding: 1mm 0;
              background: white;
            }
            
            .barcode-line {
              display: inline-block;
              height: 10mm;
              background: #000;
              margin: 0 0.3mm;
              min-width: 0.3mm;
              vertical-align: bottom;
            }
            
            .barcode-text {
              font-family: 'Courier New', monospace;
              font-size: 2mm;
              font-weight: 600;
              letter-spacing: 0.5px;
              color: #000;
              margin-top: 0.5mm;
              padding: 1mm 2mm;
              background: #f5f5f5;
              border-radius: 0.5mm;
            }
            
            /* Card Footer */
            .card-footer {
              margin-top: 3mm;
              padding-top: 2mm;
              border-top: 0.5px solid #ddd;
              font-size: 1.8mm;
              color: #666;
              text-align: center;
              line-height: 1.3;
            }
            
            .website {
              font-weight: 700;
              color: #000;
            }
            
            /* Print Info */
            .print-info {
              position: absolute;
              bottom: 1mm;
              right: 2mm;
              font-size: 1.5mm;
              color: #999;
              z-index: 4;
            }
            
            /* Container for multiple cards */
            .cards-container {
              display: grid;
              grid-template-columns: repeat(2, 85.6mm);
              gap: 5mm;
              justify-content: center;
              page-break-inside: avoid;
            }
            
            .four-per-page .cards-container {
              grid-template-columns: repeat(2, 85.6mm);
              gap: 4mm;
            }
            
            .nine-per-page .cards-container {
              grid-template-columns: repeat(3, 85.6mm);
              gap: 2mm;
            }
            
            /* Print Header */
            .print-header {
              text-align: center;
              margin-bottom: 5mm;
              padding-bottom: 3mm;
              border-bottom: 1px solid #ddd;
              font-size: 2.5mm;
              color: #666;
            }
            
            .print-title {
              font-size: 4mm;
              font-weight: 800;
              color: #000;
              margin-bottom: 1mm;
            }
            
            .print-date {
              font-weight: 600;
              margin-top: 1mm;
              color: #333;
            }
            
            /* Clear floats for printing */
            .page-break {
              page-break-after: always;
              clear: both;
            }
            
            /* Hide for printing */
            .no-print {
              display: none;
            }
            
            /* Card ID Badge */
            .card-id-badge {
              position: absolute;
              top: 12mm;
              right: 4mm;
              background: #000;
              color: white;
              padding: 0.5mm 2mm;
              border-radius: 1mm;
              font-size: 1.8mm;
              font-weight: 700;
              z-index: 4;
            }
          }
        </style>
      </head>
      <body class="${printLayout}">
        <div class="print-header">
          <div class="print-title">SILK SPACE GIFT CARDS</div>
          <div>PVC PRINT READY - PROFESSIONAL QUALITY</div>
          <div class="print-date">Printed: ${format(now, 'yyyy-MM-dd HH:mm')}</div>
          <div>Card Size: 85.6mm × 54mm • Total: ${selectedData.length} cards</div>
        </div>
        
        <div class="cards-container">
          ${selectedData.map((voucher, index) => {
            const isCredit = voucher.voucher_type === 'credit';
            const voucherId = typeof voucher.id === 'number' 
              ? `VC${String(voucher.id).padStart(6, '0')}`
              : (voucher.id || voucher.code).slice(0, 8);
            
            // Generate better barcode pattern
            const barcodeLines = Array.from({length: 40}, (_, i) => {
              const width = 0.3 + Math.random() * 1.2;
              return `<span class="barcode-line" style="width: ${width}mm;"></span>`;
            }).join('');
            
            return `
            <div class="pvc-card">
              <div class="card-background"></div>
              <div class="card-pattern"></div>
              
              <!-- Header -->
              <div class="card-header">
                <div class="card-title">SILK SPACE GIFT CARD</div>
                <div class="logo-container">
<div class="logo-container">
  <img src="/Suavemente_logo.png" alt="Silk Space" style="height: 6mm; width: auto;">
</div>                </div>
              </div>
              
              <!-- Card ID Badge -->
              <div class="card-id-badge">#${voucherId}</div>
              
              <!-- Body -->
              <div class="card-body">
                <!-- Details -->
                <div class="card-details">
                  <div class="detail-group">
                    <div class="detail-row">
                      <span class="detail-label">Type</span>
                      <span class="detail-value">${isCredit ? 'CREDIT' : 'BOOK'}</span>
                    </div>
                    
                    <div class="detail-row">
                      <span class="detail-label">Status</span>
                      <span class="detail-value value-highlight">ACTIVE</span>
                    </div>
                  </div>
                  
                  <div class="detail-group">
                    <div class="detail-row">
                      <span class="detail-label">Value</span>
                      <span class="detail-value">${isCredit ? `GHS ${parseFloat(voucher.credit_amount).toFixed(2)}` : 'PREMIUM BOOK'}</span>
                    </div>
                    
                    ${voucher.expires_at ? `
                    <div class="detail-row">
                      <span class="detail-label">Expires</span>
                      <span class="detail-value">${format(parseISO(voucher.expires_at), 'MMM dd, yyyy')}</span>
                    </div>
                    ` : `
                    <div class="detail-row">
                      <span class="detail-label">Expires</span>
                      <span class="detail-value">NO EXPIRY</span>
                    </div>
                    `}
                  </div>
                </div>
                
                <!-- Book Title for Book Vouchers -->
                ${!isCredit && voucher.book_title ? `
                <div style="text-align: center; margin: 2mm 0; padding: 2mm; background: #000; color: white; border-radius: 1.5mm;">
                  <div style="font-size: 3mm; font-weight: 700;">${voucher.book_title}</div>
                </div>
                ` : ''}
                
                <!-- Code Section -->
                <div class="code-section">
                  <div class="code-display">${voucher.code}</div>
                  ${showScratchArea ? `
                  <div class="scratch-area"></div>
                  ` : ''}
                </div>
                
                <!-- Barcode - Now More Visible -->
                ${showBarcode ? `
                <div class="barcode-section">
                  <div class="barcode-container">
                    <div class="barcode-lines">
                      ${barcodeLines}
                    </div>
                    <div class="barcode-text">${voucher.code}</div>
                  </div>
                </div>
                ` : ''}
                
                <!-- Footer -->
                <div class="card-footer">
                  Redeem at <span class="website">suavemente.com/redeem</span><br>
                  Non-transferable • Single use only • Keep this card secure
                </div>
                
                <!-- Print Info -->
                <div class="print-info">
                  ${index + 1}/${selectedData.length}
                </div>
              </div>
            </div>
            
            ${(index + 1) % cardsPerPage === 0 ? '<div class="page-break"></div>' : ''}
            `;
          }).join('')}
        </div>
        
        <script>
          window.onload = () => {
            setTimeout(() => {
              window.print();
              setTimeout(() => {
                if (!window.closed) {
                  window.close();
                }
              }, 1000);
            }, 500);
          };
          
          window.onafterprint = () => {
            setTimeout(() => {
              if (!window.closed) {
                window.close();
              }
            }, 500);
          };
        </script>
      </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  if (!isOpen || !vouchers) return null;

  const activeVouchers = vouchers.filter(v => v.status === 'active');
  const selectedCount = selectedVouchers.length;
  const totalActiveCount = activeVouchers.length;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">PVC Card Printing</h2>
              <p className="text-gray-600 mt-1">Premium Black & White PVC Cards - 85.6mm × 54mm</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-8 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Enhanced Settings Panel */}
          <div className="mb-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Printer className="h-5 w-5 mr-2 text-gray-700" />
              Premium Print Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center">
                    <Printer className="h-4 w-4 mr-1" />
                    Cards per Page
                  </span>
                </label>
                <select
                  value={printLayout}
                  onChange={(e) => setPrintLayout(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 focus:ring-2 focus:ring-gray-700 focus:border-gray-700 transition-all duration-200"
                >
                  <option value="single">1 card per page (Large)</option>
                  <option value="two-per-page">2 cards per page</option>
                  <option value="four-per-page">4 cards per page (Recommended)</option>
                  <option value="nine-per-page">9 cards per page (Dense)</option>
                </select>
                <div className="flex items-center mt-2 text-xs text-gray-500">
                  <div className="w-3 h-2 border border-gray-400 mr-2"></div>
                  Card size: 85.6mm × 54mm
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center">
                    <Filter className="h-4 w-4 mr-1" />
                    Card Features
                  </span>
                </label>
                <div className="space-y-3 bg-white p-3 rounded-lg border border-gray-200">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showBarcode}
                      onChange={(e) => setShowBarcode(e.target.checked)}
                      className="h-4 w-4 text-gray-800 rounded border-gray-300 focus:ring-gray-800"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Show Barcode</span>
                    <div className="ml-auto w-8 h-4 bg-black flex items-center justify-center">
                      <div className="h-3 w-6 bg-gradient-to-r from-black via-white to-black"></div>
                    </div>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={showScratchArea}
                      onChange={(e) => setShowScratchArea(e.target.checked)}
                      className="h-4 w-4 text-gray-800 rounded border-gray-300 focus:ring-gray-800"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Scratch-off Area</span>
                    <div className="ml-auto w-8 h-4 bg-gradient-to-r from-gray-400 to-gray-600 rounded-sm"></div>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center">
                    <Gift className="h-4 w-4 mr-1" />
                    Card Selection
                  </span>
                </label>
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={selectAllActive}
                    className="bg-gray-800 text-white px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-900 transition-colors flex items-center justify-center"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Select All ({totalActiveCount})
                  </button>
                  <button
                    onClick={clearSelection}
                    className="bg-gray-200 text-gray-700 px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-300 transition-colors flex items-center justify-center"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear All
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="flex items-center">
                    <Info className="h-4 w-4 mr-1" />
                    Print Summary
                  </span>
                </label>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Selected Cards:</span>
                      <span className="text-lg font-bold text-gray-900">{selectedCount}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Page Layout:</span>
                      <span className="font-semibold text-gray-800">{cardsPerPage} per page</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Pages:</span>
                      <span className="font-semibold text-gray-800">{Math.ceil(selectedCount / cardsPerPage)}</span>
                    </div>
                    <div className="pt-2 border-t border-gray-100">
                      <div className="text-xs text-gray-500 text-center">
                        Each card: 85.6mm × 54mm
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Premium Printing Tips */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-gray-800 rounded-full flex items-center justify-center">
                    <Info className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-semibold text-gray-900">Professional PVC Printing Guide</h4>
                  <ul className="mt-2 text-sm text-gray-700 space-y-1">
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      Use 300-350gsm PVC card stock for best durability
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      Set printer to "High Quality" or "Best" mode
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      Cards include visible barcode and scratch-off area
                    </li>
                    <li className="flex items-start">
                      <span className="text-gray-400 mr-2">•</span>
                      Cut along card borders using a PVC card cutter
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Stats Display */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-6">
              <div className="bg-gradient-to-r from-gray-900 to-black text-white px-6 py-3 rounded-2xl shadow-lg">
                <div className="text-sm font-semibold opacity-90">SELECTED</div>
                <div className="text-3xl font-bold mt-1">{selectedCount}</div>
                <div className="text-xs opacity-75 mt-1">Gift Cards</div>
              </div>
              
              <div className="bg-white border-2 border-gray-200 px-6 py-3 rounded-2xl shadow-sm">
                <div className="text-sm font-semibold text-gray-600">AVAILABLE</div>
                <div className="text-3xl font-bold text-gray-900 mt-1">{totalActiveCount}</div>
                <div className="text-xs text-gray-500 mt-1">Total Active</div>
              </div>
              
              <div className="bg-white border-2 border-gray-200 px-6 py-3 rounded-2xl shadow-sm">
                <div className="text-sm font-semibold text-gray-600">PAGES</div>
                <div className="text-3xl font-bold text-gray-900 mt-1">{Math.ceil(selectedCount / cardsPerPage)}</div>
                <div className="text-xs text-gray-500 mt-1">A4 Sheets</div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-600 font-medium">Print Date & Time</div>
              <div className="text-lg font-bold text-gray-900 font-mono">
                {format(new Date(), 'MMM dd, yyyy • HH:mm')}
              </div>
              <div className="text-xs text-gray-500 mt-1">Auto-generated</div>
            </div>
          </div>

          {/* Voucher Selection */}
          {activeVouchers.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full mb-6">
                <Gift className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No Active Vouchers</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-8">
                There are currently no active vouchers available for printing.
                Create new vouchers or activate existing ones to continue.
              </p>
              <button
                onClick={onClose}
                className="bg-gray-900 text-white px-8 py-3 rounded-xl font-semibold hover:bg-black transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Enhanced Selection Grid */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Select Vouchers to Print</h3>
                    <p className="text-gray-600 mt-1">Click on vouchers to select/deselect for printing</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Selection Progress</div>
                    <div className="text-lg font-bold text-gray-900">
                      {selectedCount} / {totalActiveCount}
                    </div>
                    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden mt-1">
                      <div 
                        className="h-full bg-gray-800 transition-all duration-300"
                        style={{ width: `${(selectedCount / totalActiveCount) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[320px] overflow-y-auto p-2">
                  {activeVouchers.map(voucher => {
                    const displayId = typeof voucher.id === 'number' 
                      ? `VC${String(voucher.id).padStart(6, '0')}`
                      : voucher.id || voucher.code.slice(0, 8);
                    
                    return (
                      <div
                        key={voucher.id}
                        onClick={() => toggleVoucher(voucher.id)}
                        className={`group cursor-pointer rounded-xl border-2 p-4 transition-all duration-200 transform hover:scale-[1.02] ${
                          selectedVouchers.includes(voucher.id)
                            ? 'border-gray-900 bg-gray-50 shadow-lg'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-3">
                              <div className="font-mono text-sm font-bold text-gray-900 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-300">
                                {voucher.code}
                              </div>
                              <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                                selectedVouchers.includes(voucher.id)
                                  ? 'bg-gray-900 border-gray-900'
                                  : 'bg-white border-gray-300'
                              }`}>
                                {selectedVouchers.includes(voucher.id) && (
                                  <Check className="h-3 w-3 text-white" />
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                                  voucher.voucher_type === 'credit'
                                    ? 'bg-gray-800 text-white'
                                    : 'bg-gray-200 text-gray-800'
                                }`}>
                                  {voucher.voucher_type === 'credit' ? '💳 CREDIT' : '📚 BOOK'}
                                </span>
                                <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                                  #{displayId}
                                </span>
                              </div>
                              
                              {voucher.voucher_type === 'book' && voucher.book_title && (
                                <div className="text-sm font-semibold text-gray-900 line-clamp-2 bg-gray-50 p-2 rounded-lg">
                                  📖 {voucher.book_title}
                                </div>
                              )}
                              
                              {voucher.voucher_type === 'credit' && (
                                <div className="text-2xl font-bold text-gray-900 bg-gradient-to-r from-gray-100 to-gray-200 p-3 rounded-xl text-center">
                                  GHS {parseFloat(voucher.credit_amount).toFixed(2)}
                                </div>
                              )}
                              
                              {voucher.expires_at && (
                                <div className="flex items-center text-xs text-gray-500 bg-white p-2 rounded-lg border border-gray-200">
                                  <Clock className="h-3 w-3 mr-2" />
                                  <span className="font-medium">Expires: {format(parseISO(voucher.expires_at), 'MMM dd, yyyy')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Premium Action Section */}
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border-2 border-gray-200 shadow-lg">
                <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
                  <div className="text-center lg:text-left">
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          Ready for Premium PVC Printing
                        </h3>
                        <p className="text-gray-600 mt-1">
                          {selectedCount} premium gift cards • {Math.ceil(selectedCount / cardsPerPage)} A4 sheets
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4 mt-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <div className="h-2 w-2 bg-gray-400 rounded-full mr-2"></div>
                        Barcode: {showBarcode ? 'Enabled' : 'Disabled'}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <div className="h-2 w-2 bg-gray-400 rounded-full mr-2"></div>
                        Scratch-off: {showScratchArea ? 'Enabled' : 'Disabled'}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <div className="h-2 w-2 bg-gray-400 rounded-full mr-2"></div>
                        Black & White Design
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      onClick={onClose}
                      className="px-8 py-4 bg-white text-gray-700 border-2 border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePrint}
                      disabled={selectedCount === 0}
                      className="px-8 py-4 bg-gradient-to-r from-gray-900 to-black text-white rounded-xl font-semibold hover:from-black hover:to-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center"
                    >
                      <Printer className="h-5 w-5 mr-3" />
                      Print PVC Gift Cards
                      {selectedCount > 0 && (
                        <span className="ml-3 bg-white/20 px-3 py-1 rounded-lg text-sm font-bold">
                          {selectedCount}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Card Preview */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Eye className="h-4 w-4 mr-2" />
                    Card Preview
                  </h4>
                  <div className="bg-gray-900/5 border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-16 w-28 border-2 border-gray-300 rounded-lg flex flex-col overflow-hidden">
                          <div className="h-6 bg-black flex items-center justify-between px-2">
                            <span className="text-[6px] text-white font-bold">SILK SPACE</span>
                            <div className="h-4 w-4 bg-white rounded-full flex items-center justify-center">
                              <span className="text-[5px] font-bold">SS</span>
                            </div>
                          </div>
                          <div className="flex-1 bg-white p-1">
                            <div className="text-[5px] font-bold text-center mb-1">GIFT CARD</div>
                            <div className="h-3 bg-gray-100 rounded mb-1"></div>
                            <div className="flex justify-between">
                              <div className="h-1 w-4 bg-black"></div>
                              <div className="h-1 w-4 bg-black"></div>
                              <div className="h-1 w-4 bg-black"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p className="font-medium">Premium PVC Gift Card</p>
                        <p className="text-xs mt-1">85.6mm × 54mm • Black & White • Professional Finish</p>
                        <div className="flex items-center space-x-2 mt-2">
                          {showBarcode && (
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">Barcode ✓</span>
                          )}
                          {showScratchArea && (
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">Scratch-off ✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Copy Success Toast
const CopyToast = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[70]">
      <div className="bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center animate-fade-in-up">
        <Check className="h-5 w-5 mr-2" />
        <span>Voucher code copied to clipboard!</span>
      </div>
    </div>
  );
};

const VoucherStatusBadge = ({ status }) => {
  const statusConfig = {
    active: { color: 'bg-green-100 text-green-800 border border-green-200', icon: CheckCircle },
    used: { color: 'bg-blue-100 text-blue-800 border border-blue-200', icon: CheckCircle },
    expired: { color: 'bg-gray-100 text-gray-800 border border-gray-200', icon: Clock },
    cancelled: { color: 'bg-red-100 text-red-800 border border-red-200', icon: XCircle }
  };

  const config = statusConfig[status] || statusConfig.active;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const VoucherTypeBadge = ({ type }) => {
  const typeConfig = {
    book: { color: 'bg-purple-100 text-purple-800', label: 'Book' },
    credit: { color: 'bg-orange-100 text-orange-800', label: 'Credit' }
  };

  const config = typeConfig[type] || typeConfig.book;

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}>
      {config.label}
    </span>
  );
};

const CreateVoucherModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    voucher_type: 'credit',
    book_id: '',
    music_id: '',
    artist_id: '',
    credit_amount: '',
    quantity: 1,
    expires_at: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState([]);
  const [booksLoading, setBooksLoading] = useState(false);
  const [artists, setArtists] = useState([]);
  const [artistMusic, setArtistMusic] = useState([]);
  const [artistsLoading, setArtistsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [createdVouchers, setCreatedVouchers] = useState(null);
  const queryClient = useQueryClient();

  // Fetch books when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBooks();
      fetchArtists();
      setFormErrors({});
    }
  }, [isOpen]);

  const fetchBooks = async () => {
    setBooksLoading(true);
    try {
      const response = await booksService.getAll({ page_size: 100 });
      setBooks(response.results || response || []);
    } catch (error) {
      console.error('Error fetching books:', error);
      setBooks([]);
    } finally {
      setBooksLoading(false);
    }
  };

  const fetchArtists = async () => {
    setArtistsLoading(true);
    try {
      const data = await voucherService.getArtists();
      setArtists(data || []);
    } catch (error) {
      console.error('Error fetching artists:', error);
      setArtists([]);
    } finally {
      setArtistsLoading(false);
    }
  };

  const handleArtistChange = async (artistId) => {
    setFormData(prev => ({ ...prev, artist_id: artistId, music_id: '' }));
    if (!artistId) { setArtistMusic([]); return; }
    try {
      const music = await voucherService.getArtistMusic(artistId);
      setArtistMusic(music || []);
    } catch {
      setArtistMusic([]);
    }
  };

  const createVoucherMutation = useMutation({
    mutationFn: voucherService.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      setCreatedVouchers(data.vouchers || data);
      setShowSuccessModal(true);
      resetForm();
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData) {
        // Handle field-specific errors
        const errors = {};
        Object.keys(errorData).forEach(key => {
          if (Array.isArray(errorData[key])) {
            errors[key] = errorData[key][0];
          } else {
            errors[key] = errorData[key];
          }
        });
        setFormErrors(errors);
      } else {
        setErrorMessage('Failed to create vouchers. Please try again.');
        setShowErrorModal(true);
      }
    }
  });

  const validateForm = () => {
    const errors = {};

    if (formData.voucher_type === 'book' && !formData.book_id) {
      errors.book_id = 'Please select a book';
    }
    if (formData.voucher_type === 'music' && !formData.music_id) {
      errors.music_id = 'Please select a music track';
    }
    if (formData.voucher_type === 'credit') {
      if (!formData.credit_amount) {
        errors.credit_amount = 'Please enter a credit amount';
      } else if (parseFloat(formData.credit_amount) <= 0) {
        errors.credit_amount = 'Credit amount must be greater than 0';
      }
    }
    if (!formData.quantity || formData.quantity < 1) {
      errors.quantity = 'Quantity must be at least 1';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setFormErrors({});
    
    try {
      // Prepare clean data for API
      const submitData = {
        voucher_type: formData.voucher_type,
        quantity: parseInt(formData.quantity),
      };

      // Add type-specific fields
      if (formData.voucher_type === 'book') {
        submitData.book_id = formData.book_id;
      } else if (formData.voucher_type === 'music') {
        submitData.music_id = formData.music_id;
      } else if (formData.voucher_type === 'credit') {
        submitData.credit_amount = parseFloat(formData.credit_amount);
      }

      // Add optional fields if they have values
      if (formData.expires_at) {
        submitData.expires_at = formData.expires_at;
      }
      if (formData.notes.trim()) {
        submitData.notes = formData.notes;
      }

      console.log('Submitting voucher data:', submitData);
      
      await createVoucherMutation.mutateAsync(submitData);
    } catch (error) {
      console.error('Error creating voucher:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      voucher_type: 'credit',
      book_id: '',
      music_id: '',
      artist_id: '',
      credit_amount: '',
      quantity: 1,
      expires_at: '',
      notes: ''
    });
    setArtistMusic([]);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedVouchers(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Create Vouchers</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Voucher Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voucher Type *
              </label>
              <select
                name="voucher_type"
                value={formData.voucher_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d]"
              >
                <option value="book">Specific Book</option>
                <option value="music">Specific Music Track</option>
                <option value="credit">Credit Amount</option>
              </select>
            </div>

            {/* Music / Artist Selection */}
            {formData.voucher_type === 'music' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Artist *
                  </label>
                  <select
                    value={formData.artist_id}
                    onChange={e => handleArtistChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d]"
                    disabled={artistsLoading}
                  >
                    <option value="">{artistsLoading ? 'Loading artists...' : 'Select an artist'}</option>
                    {artists.map(a => (
                      <option key={a.id} value={a.id}>{a.stage_name}</option>
                    ))}
                  </select>
                </div>
                {formData.artist_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Music Track *
                    </label>
                    <select
                      name="music_id"
                      value={formData.music_id}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1d1d1d] ${formErrors.music_id ? 'border-red-300' : 'border-gray-300'}`}
                    >
                      <option value="">{artistMusic.length === 0 ? 'No approved tracks' : 'Select a track'}</option>
                      {artistMusic.map(m => (
                        <option key={m.id} value={m.id}>{m.title} — GHS {parseFloat(m.price || 0).toFixed(2)}</option>
                      ))}
                    </select>
                    {formErrors.music_id && <p className="mt-1 text-sm text-red-600">{formErrors.music_id}</p>}
                    <p className="mt-1 text-xs text-gray-500">Voucher will grant free access to the selected track. Credit value equals the track price.</p>
                  </div>
                )}
              </div>
            )}

            {/* Book Selection */}
            {formData.voucher_type === 'book' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Book *
                </label>
                {booksLoading ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Loading books...
                  </div>
                ) : books.length === 0 ? (
                  <div className="text-sm text-red-500">No books available</div>
                ) : (
                  <>
                    <select
                      name="book_id"
                      value={formData.book_id}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] ${
                        formErrors.book_id ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select a book</option>
                      {books.map((book) => (
                        <option key={book.id} value={book.id}>
                          {book.title} by {book.author_name || book.author_details?.username || 'Unknown'}
                        </option>
                      ))}
                    </select>
                    {formErrors.book_id && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.book_id}</p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Credit Amount */}
            {formData.voucher_type === 'credit' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit Amount (GHS) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  name="credit_amount"
                  value={formData.credit_amount}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] ${
                    formErrors.credit_amount ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {formErrors.credit_amount && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.credit_amount}</p>
                )}
              </div>
            )}

            {/* Quantity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity *
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] ${
                  formErrors.quantity ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {formErrors.quantity && (
                <p className="mt-1 text-sm text-red-600">{formErrors.quantity}</p>
              )}
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date (Optional)
              </label>
              <input
                type="datetime-local"
                name="expires_at"
                value={formData.expires_at}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d]"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] resize-none"
                placeholder="Optional notes about this voucher..."
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#1d1d1d] text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  `Create ${formData.quantity} Voucher${formData.quantity > 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        title="Vouchers Created Successfully!"
        message={`Successfully created ${createdVouchers?.length || formData.quantity} voucher(s).`}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error Creating Vouchers"
        message={errorMessage}
      />
    </>
  );
};

const VoucherDetailsModal = ({ voucher, isOpen, onClose }) => {
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const queryClient = useQueryClient();

  const deactivateMutation = useMutation({
    mutationFn: () => voucherService.deactivate(voucher.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      onClose();
    },
    onError: () => {
      setShowErrorModal(true);
    }
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(voucher.code);
      setShowCopyToast(true);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDeactivate = () => {
    setShowConfirmModal(true);
  };

  const confirmDeactivate = () => {
    deactivateMutation.mutate();
  };

  if (!isOpen || !voucher) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-md w-full">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Voucher Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Code:</span>
              <span className="font-mono text-lg font-bold text-[#1d1d1d]">{voucher.code}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Type:</span>
              <VoucherTypeBadge type={voucher.voucher_type} />
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <VoucherStatusBadge status={voucher.status} />
            </div>

            {voucher.book_title && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Book:</span>
                <span className="text-sm text-gray-900 text-right max-w-xs">{voucher.book_title}</span>
              </div>
            )}

            {voucher.credit_amount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Credit Amount:</span>
                <span className="text-sm font-bold text-green-600">GHS {parseFloat(voucher.credit_amount).toFixed(2)}</span>
              </div>
            )}

            {voucher.expires_at && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Expires:</span>
                <span className="text-sm text-gray-900">{formatDate(voucher.expires_at)}</span>
              </div>
            )}

            {voucher.used_by_name && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Used By:</span>
                <span className="text-sm text-gray-900">{voucher.used_by_name}</span>
              </div>
            )}

            {voucher.used_at && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Used At:</span>
                <span className="text-sm text-gray-900">{formatDate(voucher.used_at)}</span>
              </div>
            )}

            {voucher.notes && (
              <div>
                <span className="text-sm font-medium text-gray-700">Notes:</span>
                <p className="text-sm text-gray-900 mt-1 bg-gray-50 p-2 rounded">{voucher.notes}</p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex space-x-3">
            <button
              onClick={handleCopyCode}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy Code
            </button>
            
            {voucher.status === 'active' && (
              <button
                onClick={handleDeactivate}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                Deactivate
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Copy Toast */}
      <CopyToast isOpen={showCopyToast} onClose={() => setShowCopyToast(false)} />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmDeactivate}
        title="Deactivate Voucher"
        message="Are you sure you want to deactivate this voucher? This action cannot be undone."
        confirmText="Deactivate"
        isDestructive={true}
      />

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error Deactivating Voucher"
        message="Failed to deactivate voucher. Please try again."
      />
    </>
  );
};

const VoucherManagement = () => {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showExportErrorModal, setShowExportErrorModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});
  const queryClient = useQueryClient();

  const { 
    data: vouchers, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['vouchers', appliedFilters],
    queryFn: () => voucherService.getAll(appliedFilters),
    retry: 2,
  });

  // Apply filters with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters(filters);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const filteredVouchers = React.useMemo(() => {
    if (!vouchers) return [];
    
    return vouchers.filter(voucher => {
      // Search filter
      const searchTerm = filters.search.toLowerCase();
      const matchesSearch = !searchTerm || 
        voucher.code.toLowerCase().includes(searchTerm) ||
        (voucher.book_title && voucher.book_title.toLowerCase().includes(searchTerm)) ||
        (voucher.used_by_name && voucher.used_by_name.toLowerCase().includes(searchTerm)) ||
        (voucher.created_by_name && voucher.created_by_name.toLowerCase().includes(searchTerm));
      
      // Status filter
      const matchesStatus = !filters.status || voucher.status === filters.status;
      
      // Type filter
      const matchesType = !filters.type || voucher.voucher_type === filters.type;
      
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [vouchers, filters]);

  const deactivateVoucherMutation = useMutation({
    mutationFn: voucherService.deactivate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
    }
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '', status: '', type: '' });
  };

  const exportVouchers = () => {
    if (!filteredVouchers || filteredVouchers.length === 0) {
      setShowExportErrorModal(true);
      return;
    }
    
    try {
      const csvContent = [
        ['Code', 'Type', 'Book', 'Credit Amount', 'Status', 'Created By', 'Used By', 'Expires At', 'Created At'],
        ...filteredVouchers.map(v => [
          v.code,
          v.voucher_type,
          v.book_title || '',
          v.credit_amount ? parseFloat(v.credit_amount).toFixed(2) : '0.00',
          v.status,
          v.created_by_name,
          v.used_by_name || '',
          v.expires_at || '',
          v.created_at
        ])
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vouchers-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting vouchers:', error);
      setShowExportErrorModal(true);
    }
  };

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setShowCopyToast(true);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const formatTableDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const activeFilterCount = Object.values(filters).filter(value => value !== '').length;

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading vouchers</h3>
          <p className="text-gray-600 font-medium mb-4">Please try again later.</p>
          <button
            onClick={() => refetch()}
            className="bg-[#1d1d1d] text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Voucher Management</h1>
            <p className="text-gray-600 font-medium">Create and manage scratch card vouchers</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPrintModal(true)}
              disabled={!vouchers || vouchers.length === 0}
              className="bg-gray-800 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Voucher Cards
            </button>
            <button
              onClick={exportVouchers}
              disabled={!filteredVouchers || filteredVouchers.length === 0}
              className="bg-gray-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#1d1d1d] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Vouchers
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Filter className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              {activeFilterCount > 0 && (
                <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                  {activeFilterCount} active
                </span>
              )}
            </div>
            <button
              onClick={handleClearFilters}
              className="text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              Clear all
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by code, book, or user..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="used">Used</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              >
                <option value="">All Types</option>
                <option value="book">Book</option>
                <option value="credit">Credit</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleClearFilters}
                className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold flex items-center justify-center"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Vouchers Table */}
        <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  All Vouchers ({vouchers?.length || 0})
                </h2>
                {activeFilterCount > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    Showing {filteredVouchers?.length || 0} filtered vouchers
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {isLoading && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </div>
                )}
                {activeFilterCount > 0 && (
                  <button
                    onClick={handleClearFilters}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <Loader className="h-8 w-8 animate-spin text-[#1d1d1d] mx-auto mb-2" />
                <p className="text-gray-600 font-medium">Loading vouchers...</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredVouchers?.map((voucher) => (
                    <tr key={voucher.id} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-mono font-bold text-[#1d1d1d] text-sm">{voucher.code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <VoucherTypeBadge type={voucher.voucher_type} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {voucher.voucher_type === 'book' ? (
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{voucher.book_title}</div>
                            {voucher.book && (
                              <div className="text-xs text-gray-500">ID: {voucher.book}</div>
                            )}
                          </div>
                        ) : (
                          <>
          <div className="text-sm font-bold text-green-600">
            Credit: GHS {parseFloat(voucher.credit_amount).toFixed(2)}
          </div>
          <div className="text-xs text-gray-500">
            Remaining: GHS {parseFloat(voucher.remaining_credit).toFixed(2)}
          </div>
        </>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <VoucherStatusBadge status={voucher.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                        {formatTableDate(voucher.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedVoucher(voucher);
                              setShowDetailsModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          
                          <button
                            onClick={() => handleCopyCode(voucher.code)}
                            className="text-gray-600 hover:text-gray-800 transition-colors p-1"
                            title="Copy code"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          
                          {voucher.status === 'active' && (
                            <button
                              onClick={() => deactivateVoucherMutation.mutate(voucher.id)}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                              title="Deactivate voucher"
                              disabled={deactivateVoucherMutation.isPending}
                            >
                              {deactivateVoucherMutation.isPending ? (
                                <Loader className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!filteredVouchers || filteredVouchers.length === 0) && !isLoading && (
            <div className="text-center py-16">
              <Gift className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {activeFilterCount > 0 ? 'No matching vouchers found' : 'No vouchers found'}
              </h3>
              <p className="text-gray-600 font-medium mb-4">
                {activeFilterCount > 0 
                  ? 'Try adjusting your search or filter criteria.' 
                  : 'Create your first voucher to get started.'}
              </p>
              {activeFilterCount > 0 ? (
                <button
                  onClick={handleClearFilters}
                  className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Clear Filters
                </button>
              ) : (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-[#1d1d1d] text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
                >
                  Create First Voucher
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateVoucherModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <VoucherDetailsModal
        voucher={selectedVoucher}
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
      />

      <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        vouchers={vouchers}
      />

      {/* Global Modals */}
      <CopyToast isOpen={showCopyToast} onClose={() => setShowCopyToast(false)} />

      <ErrorModal
        isOpen={showExportErrorModal}
        onClose={() => setShowExportErrorModal(false)}
        title="Export Error"
        message="No vouchers available to export. Please create vouchers first or adjust your filters."
      />

      {/* Deactivation Error Modal */}
      <ErrorModal
        isOpen={deactivateVoucherMutation.isError}
        onClose={() => deactivateVoucherMutation.reset()}
        title="Error Deactivating Voucher"
        message="Failed to deactivate voucher. Please try again."
      />

      {/* Add CSS for animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default VoucherManagement;